// Mantine's own colours for one slot of a seeded family (spec web/themes-engine). Mantine draws
// menus, inputs, modals and every `color="gray"` from its palettes and a few semantic colours, not
// from our tokens. This scales its gray, dark, primary (indigo) and status palettes from the seeds
// and lets Mantine derive each palette's variant colours itself (getCSSColorVariables), the dark
// slot's literal ones included. Created.ai never comes here: it keeps Mantine's own palettes.
import {
  DEFAULT_THEME,
  colorsTuple,
  getCSSColorVariables,
  mergeMantineTheme,
  type MantineColorsTuple,
} from "@mantine/core";

import { darken, lighten, mix } from "./color";
import type { Scheme, Seeds } from "./family";

/** A palette with `base` at shade 5, the filled shade theme.ts sets (primaryShade 5). */
function scaleAround(base: string): MantineColorsTuple {
  return colorsTuple([
    lighten(base, 0.9),
    lighten(base, 0.78),
    lighten(base, 0.62),
    lighten(base, 0.45),
    lighten(base, 0.25),
    base,
    darken(base, 0.14),
    darken(base, 0.28),
    darken(base, 0.42),
    darken(base, 0.56),
  ]);
}

/**
 * The light slot's gray, where Mantine reads shade 0 as the default hover, 4 as the default
 * border, 5 as the placeholder and 6 as dimmed text (default-css-variables-resolver.ts).
 */
function lightGray(s: Seeds): MantineColorsTuple {
  return colorsTuple([
    s.hover,
    mix(s.hover, s.border, 0.5),
    mix(s.border, s.surface, 0.5),
    mix(s.border, s.surface, 0.75),
    s.border,
    mix(s.muted, s.border, 0.6),
    s.muted,
    mix(s.muted, s.text, 0.5),
    s.text,
    s.strong,
  ]);
}

/**
 * The dark slot's dark, where Mantine reads shade 0 as text, 2 as dimmed, 3 as the
 * placeholder, 4 as the default border, 5 as the default hover, 6 as the default and 7 as the body.
 */
function darkScale(s: Seeds): MantineColorsTuple {
  return colorsTuple([
    s.text,
    mix(s.text, s.muted, 0.5),
    s.muted,
    mix(s.muted, s.border, 0.5),
    s.border,
    s.hover,
    mix(s.surface, s.hover, 0.5),
    s.surface,
    s.page,
    darken(s.page, 0.4),
  ]);
}

/** Mantine's status palettes, each scaled from its seed. */
const STATUS = {
  red: "danger",
  teal: "success",
  green: "success",
  blue: "info",
  cyan: "info",
  yellow: "warning",
  orange: "warning",
} as const satisfies Record<string, keyof Seeds>;

/** Mantine's variables for one slot: its palettes, then the semantic colours that are literals. */
export function mantineVariables(s: Seeds, scheme: Scheme): Record<string, string> {
  const colors: Record<string, MantineColorsTuple> = {
    indigo: scaleAround(s.primary),
    // In the dark slot `color="gray"` draws on dark surfaces (a loader's stroke, a filled badge):
    // its filled shade is the muted text, which the contrast gate keeps readable there.
    gray: scheme === "light" ? lightGray(s) : scaleAround(s.muted),
    ...(scheme === "dark" ? { dark: darkScale(s) } : {}),
  };
  for (const [color, seed] of Object.entries(STATUS)) colors[color] = scaleAround(s[seed]);
  const theme = mergeMantineTheme(DEFAULT_THEME, { colors, primaryColor: "indigo", primaryShade: 5 });
  const palettes: Record<string, string> = {};
  for (const color of Object.keys(colors)) {
    Object.assign(
      palettes,
      getCSSColorVariables({ theme, color, colorScheme: scheme, withColorValues: true }),
    );
  }
  return {
    ...palettes,
    // The light slot's body and text are Mantine's white and black literals; the dark slot's come
    // from the dark scale above. Error and success would be a darker shade than the seed, which
    // the contrast gate checked.
    ...(scheme === "light"
      ? {
          "--mantine-color-body": s.surface,
          "--mantine-color-text": s.text,
          "--mantine-color-default": s.surface,
          // The light slot paints its menus, popovers and segmented controls white: the family's.
          "--mantine-color-white": s.surface,
        }
      : {}),
    "--mantine-color-bright": s.strong,
    "--mantine-color-default-color": s.text,
    "--mantine-color-anchor": s.link,
    "--mantine-color-error": s.danger,
    "--mantine-color-success": s.success,
    "--mantine-primary-color-contrast": s.onPrimary,
  };
}
